import { Link, useNavigate, useLocation } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X, LogOut, Shield } from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/shop?category=Accessories", label: "Accessories", match: "Accessories" },
  { to: "/shop?category=Clothing", label: "Clothing", match: "Clothing" },
  { to: "/shop?category=Bags", label: "Bags", match: "Bags" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = new FormData(e.currentTarget).get("q");
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const isActive = (item) => {
    if (item.to === "/") return location.pathname === "/";
    if (item.match) {
      const qs = new URLSearchParams(location.search);
      return location.pathname === "/shop" && qs.get("category") === item.match;
    }
    if (item.to === "/shop") {
      const qs = new URLSearchParams(location.search);
      return location.pathname === "/shop" && !qs.get("category");
    }
    return false;
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-500 text-[#333E48] ${
        scrolled
          ? "backdrop-blur-xl bg-[#FDFBF7]/85 border-b border-black/10 shadow-[0_2px_30px_-12px_rgba(0,0,0,0.15)]"
          : "backdrop-blur-md bg-[#FDFBF7]/60 border-b border-transparent"
      }`}
    >
      {/* Top thin promo strip */}
      <div className="bg-black text-white text-[11px] tracking-[0.25em] uppercase py-2 text-center">
        <span>Free Nigeria-wide shipping over ₦5,000</span>
        <span className="mx-3 opacity-40">·</span>
        <span className="hidden sm:inline">Authentic Adire · Hand-dyed in Lagos</span>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20 md:h-24 gap-6">
          {/* Left: mobile + Logo */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="md:hidden p-1 -ml-1"
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <Link to="/" data-testid="brand-logo" className="flex items-center group">
              <Logo className="h-14 md:h-16 w-14 md:w-16 transition-transform duration-500 group-hover:rotate-3" />
            </Link>
          </div>

          {/* Center: nav */}
          <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
            {NAV.map((item) => {
              const active = isActive(item);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  className="relative px-4 py-2 text-[11px] tracking-[0.25em] uppercase font-semibold group"
                >
                  <span className={`relative z-10 transition-colors ${active ? "text-black" : "text-[#333E48] group-hover:text-black"}`}>
                    {item.label}
                  </span>
                  {/* Active underline */}
                  <span
                    className={`absolute left-4 right-4 -bottom-0.5 h-px bg-[#333E48] transition-transform duration-500 origin-left ${
                      active ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          {/* Right: actions */}
          <div className="flex items-center gap-1">
            <button onClick={() => setOpen((v) => !v)} aria-label="Search" data-testid="header-search-btn" className="w-10 h-10 flex items-center justify-center hover:bg-black/5 rounded-full transition-colors">
              <Search size={18} />
            </button>
            <Link to="/wishlist" aria-label="Wishlist" data-testid="header-wishlist-link" className="w-10 h-10 flex items-center justify-center hover:bg-black/5 rounded-full transition-colors">
              <Heart size={18} />
            </Link>
            <Link to="/cart" className="relative w-10 h-10 flex items-center justify-center hover:bg-black/5 rounded-full transition-colors" data-testid="header-cart-link" aria-label="Cart">
              <ShoppingBag size={18} />
              {cartCount > 0 && (
                <span
                  className="absolute top-1 right-1 bg-[#FED700] text-[#000] text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center ring-2 ring-[#FDFBF7]"
                  data-testid="cart-badge"
                >
                  {cartCount}
                </span>
              )}
            </Link>

            <div className="w-px h-6 bg-black/10 mx-2 hidden md:block" />

            {user ? (
              <div className="hidden md:flex items-center gap-1 text-sm">
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    className="px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold bg-[#FED700] text-black hover:bg-[#E5C200] transition-colors flex items-center gap-1"
                    data-testid="header-admin-link"
                  >
                    <Shield size={12} /> Admin
                  </Link>
                )}
                <Link to="/profile" className="w-10 h-10 flex items-center justify-center hover:bg-black/5 rounded-full" data-testid="header-profile-link" aria-label="Profile">
                  <User size={18} />
                </Link>
                <button onClick={logout} className="w-10 h-10 flex items-center justify-center hover:bg-black/5 rounded-full" data-testid="header-logout-btn" aria-label="Logout">
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden md:inline-flex items-center px-4 py-2 text-[10px] tracking-[0.25em] uppercase font-semibold border border-[#333E48] hover:bg-[#333E48] hover:text-white transition-all ml-2"
                data-testid="header-login-link"
              >
                Sign In
              </Link>
            )}
          </div>
        </div>

        {/* Inline search */}
        <AnimatePresence>
          {open && (
            <motion.form
              onSubmit={handleSearch}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.25 }}
              className="overflow-hidden pb-4"
              data-testid="header-search-form"
            >
              <div className="relative">
                <input
                  name="q"
                  autoFocus
                  placeholder="Search for Adire bucket hats, ties, bags…"
                  className="aj-input pr-12"
                  data-testid="header-search-input"
                />
                <button
                  type="submit"
                  aria-label="Submit search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-[#6A747D] hover:text-[#000] transition-colors"
                >
                  <Search size={16} />
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Mobile drawer */}
        <AnimatePresence>
          {menuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden overflow-hidden pb-6 flex flex-col gap-1"
              data-testid="mobile-nav"
            >
              {NAV.map((item) => (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className="px-2 py-3 border-b border-black/5 text-sm tracking-[0.2em] uppercase font-semibold"
                >
                  {item.label}
                </Link>
              ))}
              {user ? (
                <>
                  {user.role === "admin" && (
                    <Link to="/admin" onClick={() => setMenuOpen(false)} className="px-2 py-3 border-b border-black/5 text-sm tracking-[0.2em] uppercase font-semibold text-[#000] bg-[#FED700] mt-2">
                      Admin Panel
                    </Link>
                  )}
                  <Link to="/profile" onClick={() => setMenuOpen(false)} className="px-2 py-3 border-b border-black/5 text-sm tracking-[0.2em] uppercase font-semibold">Profile</Link>
                  <button onClick={() => { logout(); setMenuOpen(false); }} className="text-left px-2 py-3 text-sm tracking-[0.2em] uppercase font-semibold text-[#6A747D]">Sign Out</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setMenuOpen(false)} className="mt-2 btn-outline w-full justify-center">Sign In</Link>
              )}
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
